<template>
    <div class="w-100 row g-0 mb-2">
        <div class="col-12 col-lg-4 pe-lg-2 form-group d-flex flex-column">
            <label for="facility-name"> Facility Name </label>
            <TypeaheadInput
                :getData="getVendorList"
                :value="selectedFacility"
                v-on:valueChanged="updateFacility"
                :init-value="selectedFacility"
                placeholder="Start Typing..."
            ></TypeaheadInput>
        </div>
        <div class="col-12 col-lg-4 pe-lg-2 form-group">
            <label for="referral-address-line-2">
                Country
                <span class="text-danger">*</span>
            </label>
            <CountryTypeahead
                :updateSelectedCountry="updateSelectedCountry"
                :prefill-country="address.country"
                :disabled="selectedFacility"
                v-on:blurredInput="v$.referral[addressType].country.$touch"
                :class="`${!v$.referral[addressType].country.$error ? '' : 'field-invalid'}`"
            >
            </CountryTypeahead>
            <span v-if="v$.referral[addressType].country.$error" class="field-invalid">Country is required.</span>
        </div>
        <div class="w-100"></div>
        <div class="col-12 col-lg-4 pe-lg-2 form-group">
            <label for="referral-address-line-1">
                Address Line 1
                <span class="text-danger">*</span>
            </label>
            <PlacesAutocomplete
                v-model:street="address.addressLine1"
                v-model:city="address.city"
                v-model:state="address.state"
                v-model:zip="address.zipCode"
                v-model:county="address.county"
                v-model:latitude="address.latitude"
                v-model:longitude="address.longitude"
                v-model:disabled="selectedFacility"
                v-model:country="autoCompleteCountry"
                :places-changed-call-back="placesChangedCallBack"
                v-on:blurredInput="v$.referral[addressType].addressLine1.$touch"
                :class="`${!v$.referral[addressType].addressLine1.$error ? '' : 'field-invalid'}`"
                v-model:isValidatedAndByPassToggled="address.isValidatedAndByPassToggled"
            >
            </PlacesAutocomplete>
            <span v-if="v$.referral[addressType].addressLine1.$error" class="field-invalid"
                >Address Line 1 is required.</span
            >
        </div>
        <div class="col-12 col-lg-4 pe-lg-2 form-group">
            <label for="referral-address-line-2"> Address Line 2 </label>
            <input
                class="form-control"
                maxlength="50"
                type="text"
                v-model="address.addressLine2"
                placeholder="Enter Address Line 2"
            />
        </div>
        <div class="w-100"></div>
        <div class="col-12 col-lg-2 pe-lg-2 form-group">
            <label for="referral-city">
                City
                <span class="text-danger">*</span>
            </label>
            <input
                class="form-control"
                maxlength="50"
                type="text"
                v-model="address.city"
                placeholder="Enter City"
                :disabled="selectedFacility"
                @blur="v$.referral[addressType].city.$touch"
                :class="`${!v$.referral[addressType].city.$error ? '' : 'field-invalid'}`"
            />
            <span v-if="v$.referral[addressType].city.$error" class="field-invalid">City is required.</span>
        </div>
        <div class="col-12 col-lg-2 pe-lg-2 form-group">
            <label for="referral-state">
                {{ isUnitedStates ? "State" : "State / Province / Territory" }}
                <span class="text-danger">*</span>
            </label>
            <StateTypeahead
                v-model:selected-state="selectedState"
                v-model:prefill-state="address.state"
                :disabled="selectedFacility"
                v-if="isUnitedStates"
                v-on:blurredInput="v$.referral[addressType].state.$touch"
                :class="`${!v$.referral[addressType].state.$error ? '' : 'field-invalid'}`"
            >
            </StateTypeahead>
            <input
                class="form-control"
                maxlength="50"
                type="text"
                v-model="address.state"
                placeholder="Enter State / Province / Territory"
                :disabled="selectedFacility"
                @blur="v$.referral[addressType].state.$touch"
                :class="`${!v$.referral[addressType].state.$error ? '' : 'field-invalid'}`"
                v-else
            />
            <span v-if="v$.referral[addressType].state.$error" class="field-invalid">State is required.</span>
        </div>
        <div class="col-12 col-lg-2 pe-lg-2 form-group">
            <label for="referral-zip">
                <span class="badge me-1" v-if="showCode">A0550</span> {{ isUnitedStates ? "ZIP" : "Postal Code" }}
                <span class="text-danger">*</span>
            </label>
            <input
                class="form-control"
                maxlength="10"
                type="text"
                number-only
                :value="formatZipCode(address.zipCode)"
                @change="(e) => handleZipcodeChange(e)"
                placeholder="Enter Zip"
                :disabled="selectedFacility"
                v-if="isUnitedStates"
                @blur="v$.referral[addressType].zipCode.$touch"
                :class="`${!v$.referral[addressType].zipCode.$error ? '' : 'field-invalid'}`"
            />
            <input
                class="form-control"
                maxlength="50"
                type="text"
                v-model="address.zipCode"
                placeholder="Enter Postal Code"
                :disabled="selectedFacility"
                v-else
                @blur="v$.referral[addressType].zipCode.$touch"
                :class="`${!v$.referral[addressType].zipCode.$error ? '' : 'field-invalid'}`"
            />
            <span v-if="v$.referral[addressType].zipCode.$error" class="field-invalid">ZIP is required.</span>
        </div>
        <div class="col-12 col-lg-2 pe-lg-2 form-group">
            <label for="referral-county">
                County
                <span class="text-danger">*</span>
            </label>
            <input
                class="form-control"
                maxlength="50"
                type="text"
                v-model="address.county"
                placeholder="Enter County"
                :disabled="selectedFacility"
                @blur="v$.referral[addressType].county.$touch"
                :class="`${!v$.referral[addressType].county.$error ? '' : 'field-invalid'}`"
            />
            <span v-if="v$.referral[addressType].county.$error" class="field-invalid">County is required.</span>
        </div>
        <div class="form-group row ps-0 col-12" v-if="showMoveDate">
            <div class="form-group col-12 col-lg-3 pe-lg-2">
                <label for="date-of-movein" id="Referral_Date_Of_Birth_Label"> Move-in Date </label>
                <DatePicker :options="datePickerOptions" :getDate="handleDateChange">
                    <div class="input-group date datepickerInput">
                        <input
                            name="moveInDate"
                            id="Movein_Date"
                            type="text"
                            class="form-control"
                            placeholder="MM/DD/YYYY"
                            :value="formatDateInput(address.moveInDate)"
                            autocomplete="off"
                            date-format-only
                        />
                        <div class="input-group-addon input-group-append">
                            <div class="input-group-text">
                                <i class="fa fa-calendar"></i>
                            </div>
                        </div>
                    </div>
                </DatePicker>
            </div>
            <div class="form-group col-12 col-lg-3 pe-lg-2">
                <label for="date-of-moveout" id="Referral_Date_Of_Birth_Label"> Move-out Date </label>
                <DatePicker :getDate="handleDateChange">
                    <div class="input-group date datepickerInput">
                        <input
                            name="moveOutDate"
                            id="Moveout_Date"
                            type="text"
                            class="form-control"
                            placeholder="MM/DD/YYYY"
                            :value="formatDateInput(address.moveOutDate)"
                            autocomplete="off"
                            date-format-only
                        />
                        <div class="input-group-addon input-group-append">
                            <div class="input-group-text">
                                <i class="fa fa-calendar"></i>
                            </div>
                        </div>
                    </div>
                </DatePicker>
            </div>
        </div>
    </div>
</template>

<script lang="ts" src="./ReferralAddressForm.ts"></script>
